import React, { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { Menu, X } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const navLinksRef = useRef([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Initial Animation
    gsap.fromTo(headerRef.current,
      { y: -100, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: 'power3.out', delay: 0.5 }
    );

    // Scroll Handler for Dynamic Styling
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      // Open Menu Animation
      gsap.to(menuRef.current, {
        x: 0,
        opacity: 1,
        duration: 0.6,
        ease: 'power4.out',
        display: 'flex'
      });

      // Animate links inside
      gsap.fromTo(navLinksRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, stagger: 0.1, ease: 'power3.out', delay: 0.2 }
      );

      document.body.style.overflow = 'hidden';
    } else {
      // Close Menu Animation
      gsap.to(menuRef.current, {
        x: '100%',
        opacity: 0,
        duration: 0.4,
        ease: 'power3.in',
        onComplete: () => {
          if (menuRef.current) menuRef.current.style.display = 'none';
        }
      });
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  const navItems = [
    { name: 'About', hash: 'about' },
    { name: 'Process', hash: 'process' },
    { name: 'Bio', hash: 'bio' },
    { name: 'Services', hash: 'services' },
    { name: 'Contact', hash: 'contact' }
  ];

  const handleNavClick = (hash) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${hash}`);
    } else {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 w-full z-50 px-6 py-4 flex justify-between items-center transition-all duration-300 max-h-16 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-md shadow-sm border-b border-stone-200'
            : 'bg-transparent border-b border-white/10'
          }`}
      >
        <div
          onClick={handleLogoClick}
          className="logo flex items-center gap-3 cursor-pointer interactive group"
        >
          <div className="relative overflow-hidden rounded-full bg-white/50 backdrop-blur-sm p-1 transition-all duration-500 group-hover:bg-white/80 group-hover:shadow-lg">
            <img
              src="/amelia-logo.png"
              alt="Amelia Logo"
              className="h-10 w-10 object-contain transition-transform duration-500 group-hover:scale-110"
            />
          </div>
          <span className="font-rosarina text-2xl font-bold tracking-wider text-forest-green transition-all duration-300 group-hover:tracking-widest">
            Amelia Downie
          </span>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex space-x-8 items-center">
          {navItems.map((item) => (
            <button
              key={item.name}
              onClick={() => handleNavClick(item.hash)}
              className="font-medium tracking-wide interactive transition-all duration-300 text-forest-green hover:text-sage-mist bg-gradient-to-r from-forest-green to-forest-green bg-no-repeat bg-bottom bg-[length:0%_3%] hover:bg-[length:100%_3%]"
            >
              {item.name}
            </button>
          ))}
          <Link
            to="/blogs"
            className="font-medium tracking-wide interactive transition-all duration-300 text-forest-green hover:text-sage-mist bg-gradient-to-r from-forest-green to-forest-green bg-no-repeat bg-bottom bg-[length:0%_3%] hover:bg-[length:100%_3%]"
          >
            Blog
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden z-50 p-2 interactive transition-colors duration-300 text-forest-green focus:outline-none"
          aria-label="Toggle Menu"
        >
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </header>

      {/* Mobile Drawer Menu */}
      <div
        ref={menuRef}
        className="fixed inset-0 bg-off-white z-40 flex flex-col items-center justify-center space-y-8 md:hidden"
        style={{ display: 'none', transform: 'translateX(100%)', opacity: 0 }}
      >
        <div className="flex flex-col items-center space-y-12">
          {navItems.map((item, index) => (
            <button
              key={item.name}
              ref={el => navLinksRef.current[index] = el}
              onClick={() => handleNavClick(item.hash)}
              className="text-4xl font-serif text-forest-green hover:text-sage-mist transition-colors duration-300"
            >
              {item.name}
            </button>
          ))}
          <Link
            to="/blogs"
            ref={el => navLinksRef.current[navItems.length] = el}
            onClick={() => setIsMenuOpen(false)}
            className="text-4xl font-serif text-forest-green hover:text-sage-mist transition-colors duration-300"
          >
            Blog
          </Link>
        </div>
      </div>
    </>
  );
};

export default Header;

